/**
 * A drag-to-resize, collapsible side panel.
 *
 * Width and collapsed state persist per `storageKey` so the planner's layout
 * choice survives a reload. Width drives a CSS custom property rather than an
 * inline `width`, so the mobile breakpoint (styles.css) can cleanly override
 * it back to `auto` without fighting inline styles.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function readStored(key: string): number | null {
  const raw = window.localStorage.getItem(key)
  const value = raw ? Number(raw) : NaN
  return Number.isFinite(value) ? value : null
}

export function ResizablePanel({
  storageKey,
  title,
  defaultWidth = 380,
  minWidth = 300,
  maxWidth = 640,
  children,
}: {
  storageKey: string
  title: string
  defaultWidth?: number
  minWidth?: number
  maxWidth?: number
  children: ReactNode
}) {
  const [width, setWidth] = useState(
    () => clamp(readStored(`${storageKey}:width`) ?? defaultWidth, minWidth, maxWidth),
  )
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem(`${storageKey}:collapsed`) === '1',
  )
  const [dragging, setDragging] = useState(false)
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null)

  useEffect(() => {
    window.localStorage.setItem(`${storageKey}:width`, String(width))
  }, [width, storageKey])

  useEffect(() => {
    window.localStorage.setItem(`${storageKey}:collapsed`, collapsed ? '1' : '0')
  }, [collapsed, storageKey])

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!dragState.current) return
      // The panel sits at the right edge of the screen, so dragging the
      // handle left (negative delta) widens it.
      const delta = dragState.current.startX - event.clientX
      setWidth(clamp(dragState.current.startWidth + delta, minWidth, maxWidth))
    },
    [minWidth, maxWidth],
  )

  const endDrag = useCallback(() => {
    dragState.current = null
    setDragging(false)
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', endDrag)
  }, [onPointerMove])

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (collapsed) return
      event.preventDefault()
      dragState.current = { startX: event.clientX, startWidth: width }
      setDragging(true)
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', endDrag)
    },
    [collapsed, width, onPointerMove, endDrag],
  )

  useEffect(() => () => endDrag(), [endDrag])

  const onHandleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (collapsed) return
      const step = event.shiftKey ? 40 : 12
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setWidth((w) => clamp(w + step, minWidth, maxWidth))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setWidth((w) => clamp(w - step, minWidth, maxWidth))
      }
    },
    [collapsed, minWidth, maxWidth],
  )

  const style = { '--panel-width': `${width}px` } as CSSProperties

  return (
    <aside
      className={`workspace-side${collapsed ? ' collapsed' : ''}${dragging ? ' dragging' : ''}`}
      style={style}
    >
      <div
        className="panel-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${title} panel`}
        aria-valuenow={width}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onHandleKeyDown}
      />
      <button
        type="button"
        className="panel-collapse-toggle"
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
        title={collapsed ? `Expand ${title}` : `Collapse ${title}`}
      >
        <svg viewBox="0 0 16 16" width="11" height="11" fill="none" aria-hidden>
          <path
            d={collapsed ? 'M10 3L5 8L10 13' : 'M6 3L11 8L6 13'}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {/* Kept mounted while collapsed (hidden via CSS) so an in-progress
          chat stream, scroll position and composer draft survive a
          collapse/expand toggle instead of being torn down. */}
      <div className="panel-content">{children}</div>
      <div className="panel-collapsed-rail">
        <span>{title}</span>
      </div>
    </aside>
  )
}
